import { useState, useCallback } from 'react';
import { convertImage, getExtensionFromMime, getOutputName } from '../utils/imageOps';
import { triggerDownload } from '../utils/download';
import type { ConvertedFile, ConversionStatus, TargetMimeType } from '../types';

const INITIAL_STATUS: ConversionStatus = { type: null, message: null };

export const useBatchConvert = (files: File[]) => {
  const [targetFormat, setTargetFormat] = useState<TargetMimeType>('image/jpeg');
  const [quality, setQuality] = useState(90);
  const [resizePercent, setResizePercent] = useState(100);
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<ConversionStatus>(INITIAL_STATUS);
  const [converted, setConverted] = useState<ConvertedFile[]>([]);

  const clearStatus = useCallback(() => setStatus(INITIAL_STATUS), []);

  const clearConverted = useCallback(() => {
    setConverted((prev) => {
      prev.forEach((f) => URL.revokeObjectURL(f.url));
      return [];
    });
  }, []);

  const handleConvert = useCallback(async () => {
    if (!files.length) return;
    setIsConverting(true);
    setProgress(0);
    setStatus(INITIAL_STATUS);
    clearConverted();

    const results: ConvertedFile[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const blob = await convertImage(file, targetFormat, quality / 100, resizePercent / 100);
        const ext = getExtensionFromMime(targetFormat);
        const outName = getOutputName(file.name, targetFormat);
        results.push({
          name: outName,
          originalName: file.name,
          blob,
          url: URL.createObjectURL(blob),
          ext,
          size: blob.size,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown conversion error.';
        errors.push(message);
        console.warn('Skipping file:', message);
      }
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setConverted(results);

    if (results.length === 0) {
      setStatus({ type: 'error', message: 'No files could be converted. Check console for details.' });
    } else {
      const msg = results.length === files.length
        ? `All ${results.length} image${results.length > 1 ? 's' : ''} converted!`
        : `Converted ${results.length} of ${files.length}. ${errors.length} skipped.`;
      setStatus({ type: 'success', message: msg });
    }
    setIsConverting(false);
  }, [files, targetFormat, quality, resizePercent, clearConverted]);

  const downloadSingle = useCallback(async (item: ConvertedFile) => {
    await triggerDownload(item.url, item.name, item.blob);
  }, []);

  const downloadAll = useCallback(async () => {
    if (!converted.length) return;
    if (converted.length === 1) { await downloadSingle(converted[0]); return; }

    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const folder = zip.folder('PixBatch_Converted');
    converted.forEach((f) => folder?.file(f.name, f.blob));
    const content = await zip.generateAsync({ type: 'blob' });
    const zipUrl = URL.createObjectURL(content);
    await triggerDownload(zipUrl, `PixBatch_${Date.now()}.zip`, content);
    setTimeout(() => URL.revokeObjectURL(zipUrl), 10000);
  }, [converted, downloadSingle]);

  return {
    targetFormat, setTargetFormat,
    quality, setQuality,
    resizePercent, setResizePercent,
    isConverting, progress,
    status, clearStatus,
    converted, clearConverted,
    handleConvert, downloadAll, downloadSingle,
  };
};
